import { AlertOctagon, RefreshCw, ShieldQuestion, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertBody, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { PresentableFailure } from '@/lib/api/failure';

export interface FailureNoticeProps {
  readonly failure: PresentableFailure;
  /**
   * Offered only when the failure is retriable. A terminal failure gets no
   * retry button at all — design doc §22.4 asks for different *language*, and
   * a button that cannot work is worse than different language.
   */
  readonly onRetry?: (() => void) | undefined;
  /** The resolving action for a terminal failure, e.g. "Reload this request". */
  readonly resolveAction?: { readonly label: string; readonly onClick: () => void } | undefined;
  readonly className?: string;
}

const TONE = {
  retriable: 'warning',
  terminal: 'danger',
  'unknown-outcome': 'danger',
} as const;

const HEADING = {
  retriable: 'errors.retriableHeading',
  terminal: 'errors.terminalHeading',
  'unknown-outcome': 'errors.unknownOutcomeHeading',
} as const;

const ICON = {
  retriable: RefreshCw,
  terminal: AlertOctagon,
  'unknown-outcome': ShieldQuestion,
} as const;

/**
 * The one place an API failure becomes words.
 *
 * The three kinds are distinguished by icon, heading and available action —
 * never by colour alone (design doc §5.6).
 */
export function FailureNotice({ failure, onRetry, resolveAction, className }: FailureNoticeProps) {
  const { t } = useTranslation();
  const Icon = ICON[failure.kind];

  return (
    <Alert
      tone={TONE[failure.kind]}
      role="alert"
      className={className}
      data-failure-kind={failure.kind}
      data-failure-code={failure.code}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <AlertBody className="flex flex-col gap-2">
        <AlertTitle>{t(HEADING[failure.kind])}</AlertTitle>
        <p>{t(failure.guidanceKey)}</p>
        {failure.detail ? <p className="opacity-90">{failure.detail}</p> : null}
        <p className="text-xs opacity-75">
          {t('common.errorCode', { code: failure.code })}
          {failure.requestId ? ` · ${t('common.requestId', { id: failure.requestId })}` : ''}
        </p>
        <div className="flex flex-wrap gap-2">
          {failure.kind === 'retriable' && onRetry ? (
            <Button size="sm" variant="outline" onClick={onRetry}>
              <RefreshCw aria-hidden="true" />
              {t('common.retry')}
            </Button>
          ) : null}
          {resolveAction ? (
            <Button size="sm" variant="outline" onClick={resolveAction.onClick}>
              {resolveAction.label}
            </Button>
          ) : null}
        </div>
      </AlertBody>
    </Alert>
  );
}

/** Inline variant for a failure attached to one control rather than a whole screen. */
export function InlineFailure({ failure }: { readonly failure: PresentableFailure }) {
  const { t } = useTranslation();
  return (
    <p
      role="alert"
      className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400"
      data-failure-code={failure.code}
    >
      <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <span>
        {t(failure.guidanceKey)} {failure.detail}
      </span>
    </p>
  );
}
